import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Input,
  Button,
  Card,
  Tabs,
  Table,
  Tag,
  Spin,
  Alert,
  Typography,
  Space,
  Descriptions,
  Collapse,
  Divider,
  Drawer,
  Badge,
  List,
  Radio,
  Steps,
  Select
} from 'antd';
import {
  SearchOutlined,
  FileTextOutlined,
  AlertOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  MessageOutlined,
  SendOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const AFC_URL_PATTERN = /\/preview\/(\d+)/;

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const escapeCsv = (val) => {
  const str = String(val == null ? '' : val);
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const exportFullJson = (typologies, riskIndicators, explanationGraph, dataModelFields, indicatorLogics) => {
  const data = { typologies, riskIndicators, explanationGraph, dataModelFields, indicatorLogics };
  downloadFile(JSON.stringify(data, null, 2), 'typology-analysis.json', 'application/json');
};

const exportOverviewCsv = (typologies) => {
  const headers = ['ID', 'Title', 'Description', 'Location', 'Theme', 'Technique', 'Industry', 'Predicate Offence', 'Segment', 'Financial Instrument', 'Country'];
  const rows = typologies.map((t) => [
    t.id,
    escapeCsv(t.title),
    escapeCsv(t.description),
    escapeCsv((t.location || []).join('; ')),
    escapeCsv((t.theme || []).join('; ')),
    escapeCsv((t.technique || []).join('; ')),
    escapeCsv((t.industry || []).join('; ')),
    escapeCsv((t.predicateOffence || []).join('; ')),
    escapeCsv((t.segment || []).join('; ')),
    escapeCsv((t.financialInstrument || []).join('; ')),
    escapeCsv((t.country || []).join('; '))
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  downloadFile(csv, 'typology-overview.csv', 'text/csv');
};

const exportRiskIndicatorsCsv = (riskIndicators) => {
  if (!riskIndicators) return;
  const { typologyFeatureMap = {}, businessTechFeatureMap = {} } = riskIndicators;

  // Sheet 1: Risk indicators per typology
  const riHeaders = ['Typology ID', 'Risk Indicator'];
  const riRows = [];
  Object.entries(typologyFeatureMap).forEach(([typoId, indicators]) => {
    indicators.forEach((ind) => {
      riRows.push([typoId, escapeCsv(ind)]);
    });
  });

  // Sheet 2: Business to technical name mapping
  const mapHeaders = ['Business Name', 'Technical Name'];
  const mapRows = Object.entries(businessTechFeatureMap).map(([biz, tech]) => [
    escapeCsv(biz),
    escapeCsv(tech)
  ]);

  const csv = [
    '--- Risk Indicators by Typology ---',
    riHeaders.join(','),
    ...riRows.map((r) => r.join(',')),
    '',
    '--- Business to Technical Name Mapping ---',
    mapHeaders.join(','),
    ...mapRows.map((r) => r.join(','))
  ].join('\n');
  downloadFile(csv, 'risk-indicators.csv', 'text/csv');
};

const exportDataModelCsv = (dataModelFields, typologyFieldMap) => {
  if (!dataModelFields || dataModelFields.length === 0) return;
  const fieldToTypologies = buildFieldToTypologyMap(typologyFieldMap);
  const headers = ['Table Name', 'Field Name', 'Data Type', 'Nullable', 'Description', 'Mandatory', 'Typology Field', 'UI Field', 'Internal', 'Typology IDs'];
  const rows = dataModelFields.map((f) => {
    const field = f.field || {};
    const key = `${field.tableName || ''}.${field.name || ''}`;
    const typoIds = fieldToTypologies[key] ? fieldToTypologies[key].join('; ') : '';
    return [
      escapeCsv(field.tableName || ''),
      escapeCsv(field.name || ''),
      escapeCsv(field.dataType || ''),
      field.nullable ? 'Yes' : 'No',
      escapeCsv(field.description || ''),
      f.mandatoryField ? 'Yes' : 'No',
      f.typologyField ? 'Yes' : 'No',
      f.uiField ? 'Yes' : 'No',
      field.isInternal ? 'Yes' : 'No',
      escapeCsv(typoIds)
    ];
  });
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  downloadFile(csv, 'data-model-requirements.csv', 'text/csv');
};

const exportExplanationGraphCsv = (explanationGraph) => {
  if (!explanationGraph) return;
  const { englishName = {} } = explanationGraph;
  const headers = ['Typology', 'Technical Name', 'English Name'];
  const rows = [];
  Object.entries(englishName).forEach(([typology, pairs]) => {
    pairs.forEach(([tech, eng]) => {
      rows.push([escapeCsv(typology), escapeCsv(tech), escapeCsv(eng)]);
    });
  });
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  downloadFile(csv, 'explanation-graph.csv', 'text/csv');
};

const exportKeyLogicCsv = (indicatorLogics) => {
  if (!indicatorLogics || indicatorLogics.length === 0) return;
  const headers = [
    'Indicator Name', 'Business Name', 'Typology ID', 'Main Type', 'Sub Type',
    'Template Function', 'Lookback Days', 'SQL Expression', 'Dynamic Properties'
  ];
  const rows = indicatorLogics.map((il) => [
    escapeCsv(il.name || ''),
    escapeCsv(il.businessName || ''),
    escapeCsv(il.typologyId || ''),
    escapeCsv(il.mainType || ''),
    escapeCsv(il.subType || ''),
    escapeCsv(il.templateFunc || ''),
    escapeCsv((il.lookbackDays || []).join(', ')),
    escapeCsv(il.expressionSql || ''),
    escapeCsv((il.relatedDynProps || []).map((d) => `${d.name}: ${d.englishName}`).join('; '))
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  downloadFile(csv, 'indicator-key-logic.csv', 'text/csv');
};

const extractTypologyIds = (input) => {
  const ids = [];
  // Split by newlines, commas, or spaces
  const parts = input.split(/[\n,\s]+/).filter(Boolean);
  for (const part of parts) {
    const urlMatch = part.match(AFC_URL_PATTERN);
    if (urlMatch) {
      ids.push(parseInt(urlMatch[1], 10));
    } else {
      const num = parseInt(part.trim(), 10);
      if (!isNaN(num) && num > 0) {
        ids.push(num);
      }
    }
  }
  return [...new Set(ids)]; // dedupe
};

const NODE_TYPE_LABELS = { 1: 'Individual', 2: 'Corporate', 3: 'Any' };
const NODE_TYPE_COLORS = { 1: 'blue', 2: 'orange', 3: 'default' };

// Available attribute keys/values for adding new properties
const AVAILABLE_NODE_ATTRS = {
  'Customer or Counterparty': ['Customer', 'Counterparty'],
  'Account Type': ['Individual', 'Corporate', 'Any Account Type'],
  'Transaction Recency': ['Last 1 yr', 'Last 6 months', 'Last 5 yrs'],
  'Is Offshore': ['Yes', 'No'],
  'High Risk Location': ['Yes', 'No'],
  'Is PEP': ['Yes', 'No'],
  'Adverse News': ['Yes', 'No'],
  'Bankrupt': ['Yes', 'No'],
  'Complaint Submitted': ['Yes', 'No'],
  'Customer Age Group': ['Young', 'Elder'],
  'Risk Level': ['Low', 'Medium', 'High'],
  'new relationship': ['Yes'],
  'new account': ['Yes'],
  'dormant account': ['Yes'],
  'Unemployed': ['Yes'],
  'STR': ['Yes'],
  'Customer Categorisation': ['PEP', 'Financial Inclusion'],
  'Number of KYC Changes': ['Yes'],
};

const AVAILABLE_REL_ATTRS = {
  'Transaction Direction': ['Customer to Counterparty', 'Counterparty to Customer', 'Both Ways'],
  'Transaction Value': ['High', 'Medium', 'Low'],
  'Incoming Transactions Frequency': ['High', 'Medium', 'Low'],
  'Outgoing Transactions Frequency': ['High', 'Medium', 'Low'],
  'Sum Of Incoming Transactions': ['High', 'Medium', 'Low'],
  'Sum Of Outgoing Transactions': ['High', 'Medium', 'Low'],
  'Deviation From Historical Behaviour': ['Income', 'Monthly Account Balance', 'Deviation from self', 'Nature of Expenses', 'Voucher Consumption Rate'],
  'Transaction Below Threshold': ['Yes', 'No'],
  'Multiple Transactions of Similar Amounts': ['Yes', 'No'],
  'High Transaction Flow Through': ['Yes', 'No'],
  'Transaction Directed to High Risk Countries': ['Yes', 'No'],
  'Transactions Directed Towards Tax Haven Countries': ['Yes', 'No'],
  'Multiple Outgoing Overseas Transactions': ['Yes', 'No'],
  'Volume Of Overseas Transactions': ['High', 'Medium', 'Low'],
  'Transaction Instrument': ['Ewallet', 'Cash', 'Cheque', 'Fund Transfer', 'Card Payment', 'Loan'],
  'Transaction Recency': ['Last 1 yr', 'Last 6 months', 'Last 5 yrs'],
  'sudden surge of transactions': ['yes'],
  'Circular Transactions Present': ['Yes'],
  'No Transaction Purpose': ['Yes'],
  'Within a specified transaction window': ['Yes'],
  'Deviation from KYC Payment Amount': ['Yes'],
  'Deviation from KYC Payment Frequency': ['Yes'],
  'Deviation Behaviour Nature Expenses': ['Yes'],
};

const AttrEditor = ({ attributes, availableAttrs, onChange }) => {
  const [addKey, setAddKey] = useState('');
  const [addVal, setAddVal] = useState('');

  const handleRemove = (key) => {
    const updated = { ...attributes };
    delete updated[key];
    onChange(updated);
  };

  const handleAdd = () => {
    if (addKey && addVal) {
      onChange({ ...attributes, [addKey]: addVal });
      setAddKey('');
      setAddVal('');
    }
  };

  const unusedKeys = Object.keys(availableAttrs).filter((k) => !(k in attributes));

  return (
    <div style={{ fontSize: 12 }}>
      {Object.entries(attributes).map(([key, val]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 2, gap: 4 }}>
          <Button type="text" size="small" danger style={{ padding: 0, minWidth: 16, height: 16, fontSize: 10 }}
            onClick={() => handleRemove(key)}>x</Button>
          <Text type="secondary">{key}:</Text>
          <Text strong>{Array.isArray(val) ? val.join(', ') : String(val)}</Text>
        </div>
      ))}
      {unusedKeys.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <Select size="small" style={{ flex: 1 }} placeholder="Add property..." value={addKey || undefined}
            onChange={(v) => { setAddKey(v); setAddVal(''); }}
            options={unusedKeys.map((k) => ({ value: k, label: k }))} />
          {addKey && availableAttrs[addKey] && (
            <Select size="small" style={{ width: 160 }} placeholder="Value" value={addVal || undefined}
              onChange={setAddVal}
              options={availableAttrs[addKey].map((v) => ({ value: v, label: v }))} />
          )}
          <Button size="small" type="primary" disabled={!addKey || !addVal} onClick={handleAdd}>+</Button>
        </div>
      )}
    </div>
  );
};

const TypologyInfoCard = ({ typology, typologyStructure, onStructureChange }) => {
  const nodes = typologyStructure?.nodes || [];
  const relations = typologyStructure?.relations || [];
  const nodeMap = {};
  nodes.forEach((n) => { nodeMap[n.id] = n.name; });

  const updateNodeAttrs = (nodeIdx, newAttrs) => {
    if (!onStructureChange) return;
    const updated = { ...typologyStructure };
    updated.nodes = [...updated.nodes];
    updated.nodes[nodeIdx] = { ...updated.nodes[nodeIdx], attributes: newAttrs };
    onStructureChange(updated);
  };

  const updateRelAttrs = (relIdx, newAttrs) => {
    if (!onStructureChange) return;
    const updated = { ...typologyStructure };
    updated.relations = [...updated.relations];
    updated.relations[relIdx] = { ...updated.relations[relIdx], attributes: newAttrs };
    onStructureChange(updated);
  };

  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Descriptions
        title={`[AFC-${typology.id}] ${typology.title}`}
        column={2}
        size="small"
        bordered
      >
        <Descriptions.Item label="Description" span={2}>
          {typology.description}
        </Descriptions.Item>
        <Descriptions.Item label="Location">
          {(typology.location || []).map((l) => (
            <Tag key={l} color="blue">{l}</Tag>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label="Theme">
          {(typology.theme || []).map((t) => (
            <Tag key={t} color="green">{t}</Tag>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label="Technique">
          {(typology.technique || []).map((t) => (
            <Tag key={t} color="orange">{t}</Tag>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label="Industry">
          {(typology.industry || []).map((i) => (
            <Tag key={i}>{i}</Tag>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label="Predicate Offence" span={2}>
          {(typology.predicateOffence || []).map((p) => (
            <Tag key={p} color="red">{p}</Tag>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label="Segment">
          {(typology.segment || []).map((s) => (
            <Tag key={s} color="purple">{s}</Tag>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label="Financial Instrument">
          {(typology.financialInstrument || []).map((f) => (
            <Tag key={f} color="cyan">{f}</Tag>
          ))}
        </Descriptions.Item>
      </Descriptions>

      {nodes.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Divider orientation="left" style={{ fontSize: 13 }}>
            <ApartmentOutlined /> Graph Structure ({nodes.length} nodes, {relations.length} relations)
          </Divider>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {nodes.map((node) => (
              <Card
                key={node.id}
                size="small"
                style={{
                  minWidth: 250, maxWidth: 350,
                  border: `2px solid ${NODE_TYPE_COLORS[node.nodeType] === 'blue' ? '#1890ff' : NODE_TYPE_COLORS[node.nodeType] === 'orange' ? '#fa8c16' : '#d9d9d9'}`
                }}
                title={
                  <span>
                    <Tag color={NODE_TYPE_COLORS[node.nodeType] || 'default'}>
                      {NODE_TYPE_LABELS[node.nodeType] || 'Unknown'}
                    </Tag>
                    <strong>{node.name}</strong>
                  </span>
                }
              >
                {onStructureChange ? (
                  <AttrEditor
                    attributes={node.attributes || {}}
                    availableAttrs={AVAILABLE_NODE_ATTRS}
                    onChange={(newAttrs) => updateNodeAttrs(nodes.indexOf(node), newAttrs)}
                  />
                ) : (
                  node.attributes && Object.keys(node.attributes).length > 0 ? (
                    <div style={{ fontSize: 12 }}>
                      {Object.entries(node.attributes).map(([key, val]) => (
                        <div key={key} style={{ marginBottom: 2 }}>
                          <Text type="secondary">{key}:</Text>{' '}
                          <Text strong>{Array.isArray(val) ? val.join(', ') : String(val)}</Text>
                        </div>
                      ))}
                    </div>
                  ) : <Text type="secondary" style={{ fontSize: 12 }}>No attributes</Text>
                )}
              </Card>
            ))}
          </div>

          {relations.length > 0 && (
            <Table
              size="small"
              bordered
              pagination={false}
              columns={[
                {
                  title: 'From', dataIndex: 'from', key: 'from', width: 150,
                  render: (_, r) => <Tag>{nodeMap[r.firstNodeId] || r.firstNodeId}</Tag>
                },
                {
                  title: '', key: 'arrow', width: 30,
                  render: () => <span style={{ fontSize: 16 }}>→</span>
                },
                {
                  title: 'To', dataIndex: 'to', key: 'to', width: 150,
                  render: (_, r) => <Tag>{nodeMap[r.secondNodeId] || r.secondNodeId}</Tag>
                },
                {
                  title: 'Type', dataIndex: 'relationType', key: 'type', width: 130,
                  render: (v) => <Tag color={v === 'Transactional' ? 'green' : 'blue'}>{v}</Tag>
                },
                {
                  title: 'Attributes', key: 'attrs',
                  render: (_, r) => {
                    const attrs = r.attributes || {};
                    if (onStructureChange && r.relationType === 'Transactional') {
                      const relIdx = relations.findIndex((rel) => rel.id === r.id);
                      return (
                        <AttrEditor
                          attributes={attrs}
                          availableAttrs={AVAILABLE_REL_ATTRS}
                          onChange={(newAttrs) => updateRelAttrs(relIdx, newAttrs)}
                        />
                      );
                    }
                    return Object.keys(attrs).length > 0 ? (
                      <div style={{ fontSize: 11 }}>
                        {Object.entries(attrs).map(([k, v]) => (
                          <div key={k}>
                            <Text type="secondary">{k}:</Text>{' '}
                            <Text>{Array.isArray(v) ? v.join(', ') : String(v)}</Text>
                          </div>
                        ))}
                      </div>
                    ) : <Text type="secondary">—</Text>;
                  }
                }
              ]}
              dataSource={relations.map((r, i) => ({ ...r, key: i }))}
            />
          )}
        </div>
      )}
    </Card>
  );
};

const RiskIndicatorsTab = ({ riskIndicators }) => {
  if (!riskIndicators) return <Alert message="No risk indicators data" type="info" />;

  const { typologyFeatureMap = {}, businessTechFeatureMap = {} } = riskIndicators;

  const typologyColumns = [
    { title: 'Typology', dataIndex: 'typology', key: 'typology', width: 300 },
    {
      title: 'Risk Indicators',
      dataIndex: 'indicators',
      key: 'indicators',
      render: (indicators) => (
        <div>
          {indicators.map((ind, idx) => (
            <Tag key={idx} style={{ marginBottom: 4 }}>{ind}</Tag>
          ))}
        </div>
      )
    }
  ];

  const typologyData = Object.entries(typologyFeatureMap).map(([typology, indicators], idx) => ({
    key: idx,
    typology,
    indicators
  }));

  const mappingColumns = [
    { title: 'Business Name', dataIndex: 'business', key: 'business' },
    { title: 'Technical Name', dataIndex: 'technical', key: 'technical' }
  ];

  const mappingData = Object.entries(businessTechFeatureMap).map(([business, technical], idx) => ({
    key: idx,
    business,
    technical
  }));

  return (
    <div>
      <Title level={5}>Risk Indicators by Typology</Title>
      <Table
        columns={typologyColumns}
        dataSource={typologyData}
        pagination={false}
        size="small"
        bordered
        style={{ marginBottom: 24 }}
      />

      <Divider />

      <Title level={5}>Business to Technical Name Mapping</Title>
      <Table
        columns={mappingColumns}
        dataSource={mappingData}
        pagination={{ pageSize: 20 }}
        size="small"
        bordered
      />
    </div>
  );
};

const ExplanationGraphTab = ({ explanationGraph }) => {
  if (!explanationGraph) return <Alert message="No explanation graph data" type="info" />;

  const { englishName = {} } = explanationGraph;

  return (
    <div>
      <Title level={5}>Explanation Graph - English Names</Title>
      <Collapse>
        {Object.entries(englishName).map(([typology, pairs]) => (
          <Panel header={typology} key={typology}>
            <Table
              columns={[
                { title: 'Technical Name', dataIndex: 'technical', key: 'technical' },
                { title: 'English Name', dataIndex: 'english', key: 'english' }
              ]}
              dataSource={pairs.map(([tech, eng], idx) => ({
                key: idx,
                technical: tech,
                english: eng
              }))}
              pagination={false}
              size="small"
              bordered
            />
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

const buildFieldToTypologyMap = (typologyFieldMap) => {
  const fieldToTypologies = {};
  if (!typologyFieldMap) return fieldToTypologies;
  Object.entries(typologyFieldMap).forEach(([typoId, fields]) => {
    (fields || []).forEach((f) => {
      const key = `${f.field?.tableName || ''}::${f.field?.name || ''}`;
      if (!fieldToTypologies[key]) fieldToTypologies[key] = [];
      if (!fieldToTypologies[key].includes(typoId)) {
        fieldToTypologies[key].push(typoId);
      }
    });
  });
  return fieldToTypologies;
};

const DataModelTab = ({ dataModelFields, typologyFieldMap }) => {
  if (!dataModelFields || dataModelFields.length === 0) {
    return <Alert message="No data model requirements data" type="info" />;
  }

  const fieldToTypologies = buildFieldToTypologyMap(typologyFieldMap);

  // Group fields by table name
  const tableGroups = {};
  dataModelFields.forEach((f) => {
    const tableName = (f.field && f.field.tableName) || 'Unknown';
    if (!tableGroups[tableName]) tableGroups[tableName] = [];
    tableGroups[tableName].push(f);
  });

  const columns = [
    { title: 'Field Name', dataIndex: 'name', key: 'name', width: 250,
      render: (text, record) => (
        <span>
          {text}
          {record.mandatory && <Tag color="red" style={{ marginLeft: 4 }}>Required</Tag>}
          {record.isPartition && <Tag color="gold" style={{ marginLeft: 4 }}>Partition</Tag>}
        </span>
      )
    },
    { title: 'Data Type', dataIndex: 'dataType', key: 'dataType', width: 120 },
    { title: 'Nullable', dataIndex: 'nullable', key: 'nullable', width: 80,
      render: (v) => v ? 'Yes' : 'No'
    },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Typologies', dataIndex: 'typologyIds', key: 'typologyIds', width: 150,
      render: (ids) => ids && ids.length > 0
        ? ids.map((id) => <Tag key={id} color="blue">AFC-{id}</Tag>)
        : <Text type="secondary">All</Text>
    },
    { title: 'Flags', dataIndex: 'flags', key: 'flags', width: 200,
      render: (_, record) => (
        <span>
          {record.typologyField && <Tag color="blue">Typology</Tag>}
          {record.uiField && <Tag color="green">UI</Tag>}
          {record.internal && <Tag>Internal</Tag>}
          {record.affectByDynProp && <Tag color="orange">Dynamic</Tag>}
        </span>
      )
    }
  ];

  return (
    <div>
      <Title level={5}>Data Model Requirements</Title>
      <Paragraph type="secondary">
        Tables and fields required to support the selected typologies. {dataModelFields.length} fields across {Object.keys(tableGroups).length} tables.
      </Paragraph>
      <Collapse defaultActiveKey={Object.keys(tableGroups).slice(0, 3)}>
        {Object.entries(tableGroups).map(([tableName, fields]) => (
          <Panel
            header={<span><DatabaseOutlined style={{ marginRight: 8 }} />{tableName} ({fields.length} fields)</span>}
            key={tableName}
          >
            <Table
              columns={columns}
              dataSource={fields.map((f, idx) => {
                const fieldKey = `${f.field?.tableName || ''}::${f.field?.name || ''}`;
                return {
                  key: idx,
                  name: f.field?.name || '',
                  dataType: f.field?.dataType || '',
                  nullable: f.field?.nullable,
                  description: f.field?.description || '',
                  isPartition: f.field?.isPartition,
                  internal: f.field?.isInternal,
                  mandatory: f.mandatoryField,
                  typologyField: f.typologyField,
                  uiField: f.uiField,
                  affectByDynProp: f.affectByDynProp,
                  typologyIds: fieldToTypologies[fieldKey] || []
                };
              })}
              pagination={false}
              size="small"
              bordered
            />
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

const KeyLogicTab = ({ indicatorLogics }) => {
  if (!indicatorLogics || indicatorLogics.length === 0) {
    return <Alert message="No indicator logic data available" type="info" />;
  }

  // Group by typology
  const byTypology = {};
  indicatorLogics.forEach((il) => {
    const tid = il.typologyId || 'Unknown';
    if (!byTypology[tid]) byTypology[tid] = [];
    byTypology[tid].push(il);
  });

  const columns = [
    {
      title: 'Indicator', dataIndex: 'name', key: 'name', width: 280,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 12 }}>{text}</div>
          {record.businessName && (
            <div style={{ color: '#666', fontSize: 11 }}>{record.businessName}</div>
          )}
        </div>
      )
    },
    {
      title: 'Type', key: 'type', width: 180,
      render: (_, record) => (
        <div>
          <Tag>{record.mainType || 'N/A'}</Tag>
          {record.subType && <Tag color="blue">{record.subType}</Tag>}
        </div>
      )
    },
    {
      title: 'Template', dataIndex: 'templateFunc', key: 'templateFunc', width: 180,
      render: (v) => v ? <Tag color="purple">{v}</Tag> : <Text type="secondary">-</Text>
    },
    {
      title: 'Lookback', dataIndex: 'lookbackDays', key: 'lookbackDays', width: 120,
      render: (days) => days && days.length > 0
        ? days.map((d) => <Tag key={d} color="cyan">{d}d</Tag>)
        : <Text type="secondary">-</Text>
    },
    {
      title: 'SQL Expression', dataIndex: 'expressionSql', key: 'expressionSql',
      render: (sql) => sql ? (
        <div style={{
          fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap',
          maxHeight: 100, overflow: 'auto', background: '#f5f5f5', padding: 4, borderRadius: 4
        }}>
          {sql}
        </div>
      ) : <Text type="secondary">-</Text>
    },
    {
      title: 'Dynamic Properties', key: 'dynProps', width: 220,
      render: (_, record) => {
        const dps = record.relatedDynProps || [];
        return dps.length > 0 ? (
          <div>
            {dps.map((dp, idx) => (
              <div key={idx} style={{ marginBottom: 2 }}>
                <Tag color="orange" style={{ fontSize: 11 }}>{dp.name}</Tag>
                <div style={{ fontSize: 10, color: '#888' }}>{dp.englishName}</div>
                {dp.defaultValue && (
                  <div style={{ fontSize: 10, color: '#999' }}>Default: {dp.defaultValue}</div>
                )}
              </div>
            ))}
          </div>
        ) : <Text type="secondary">-</Text>;
      }
    }
  ];

  return (
    <div>
      <Title level={5}>Risk Indicator Key Logic</Title>
      <Paragraph type="secondary">
        Detailed computation logic for each risk indicator, including SQL expressions,
        aggregation types, lookback periods, and configurable dynamic properties.
      </Paragraph>
      <Collapse defaultActiveKey={Object.keys(byTypology).slice(0, 2)}>
        {Object.entries(byTypology).map(([typologyId, indicators]) => (
          <Panel
            header={
              <span>
                <ExperimentOutlined style={{ marginRight: 8 }} />
                Typology {typologyId} ({indicators.length} indicators)
              </span>
            }
            key={typologyId}
          >
            <Table
              columns={columns}
              dataSource={indicators.map((il, idx) => ({ ...il, key: idx }))}
              pagination={false}
              size="small"
              bordered
              scroll={{ x: 1200 }}
            />
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

const TypologyChatPanel = ({ visible, onClose, analysisData, agentType = 'typology_qa', onRefinementResult }) => {
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!inputMsg.trim() || isSending) return;

    const userMsg = inputMsg.trim();
    setInputMsg('');
    setMessages((prev) => [...prev, { role: 'user', content: userMsg }]);
    setIsSending(true);

    try {
      const agentApi = localStorage.getItem('service-api-agent') || 'http://localhost:8090';
      const token = localStorage.getItem('id_token') || '';
      const anthropicKey = localStorage.getItem('anthropic-api-key') || '';
      const chatHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      if (anthropicKey) {
        chatHeaders['X-Anthropic-Api-Key'] = anthropicKey;
      }
      const response = await fetch(`${agentApi}/chat`, {
        method: 'POST',
        headers: chatHeaders,
        body: JSON.stringify({
          message: userMsg,
          session_id: sessionId,
          context: {
            agent_type: agentType,
            source_module: 'TM-IAD',
            extra: {
              analysis_data: analysisData,
              ...(analysisData?._engineUrl ? { engine_url: analysisData._engineUrl } : {}),
              ...(analysisData?._caasUrl ? { caas_url: analysisData._caasUrl } : {}),
              ...(analysisData?._afcToken ? { afc_token: analysisData._afcToken } : {}),
              ...(analysisData?._typologyStructure ? { typology_structure: analysisData._typologyStructure } : {}),
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Chat API error: ${response.status}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let buffer = '';

      // Add empty assistant message to stream into
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: session')) {
            const dataLine = lines[lines.indexOf(line) + 1];
            if (dataLine && dataLine.startsWith('data: ')) {
              try {
                const data = JSON.parse(dataLine.substring(6));
                if (data.session_id) setSessionId(data.session_id);
              } catch (e) { /* ignore */ }
            }
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.content) {
                assistantContent += data.content;
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: assistantContent };
                  return updated;
                });
              }
              if (data.session_id) setSessionId(data.session_id);
              // Check for refinement result (done event with typology data)
              if (data.data && data.agent_name && onRefinementResult) {
                const resultData = data.data;
                if (resultData.riskIndicators || resultData.typologies) {
                  onRefinementResult(resultData, data.summary || '');
                }
              }
            } catch (e) { /* ignore parse errors for non-JSON lines */ }
          }
        }
      }

      // Ensure final content is set
      if (assistantContent) {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: assistantContent };
          return updated;
        });
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev.filter((m) => m.content !== ''),
        { role: 'assistant', content: `Error: ${err.message}. Make sure the agent service is running on port 8090.` }
      ]);
    } finally {
      setIsSending(false);
    }
  }, [inputMsg, isSending, sessionId, analysisData, agentType, onRefinementResult]);

  return (
    <Drawer
      title={
        <span>
          <MessageOutlined style={{ marginRight: 8 }} />
          Typology Q&A
        </span>
      }
      placement="right"
      width={520}
      open={visible}
      onClose={onClose}
      bodyStyle={{ display: 'flex', flexDirection: 'column', padding: 0 }}
    >
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>
            <MessageOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div>Ask questions about the analyzed typologies.</div>
            <div style={{ fontSize: 12, marginTop: 8 }}>
              Examples:
              <ul style={{ textAlign: 'left', marginTop: 8 }}>
                <li>Is the matching logic exact, fuzzy, or contains-based?</li>
                <li>What dynamic properties can be configured?</li>
                <li>What data fields are needed for this typology?</li>
                <li>Explain the risk indicators and their aggregation logic</li>
              </ul>
            </div>
          </div>
        )}
        <List
          dataSource={messages}
          renderItem={(msg, idx) => (
            <List.Item
              key={idx}
              style={{
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                border: 'none', padding: '4px 0'
              }}
            >
              <div style={{
                maxWidth: '85%',
                padding: '8px 12px',
                borderRadius: 8,
                background: msg.role === 'user' ? '#1890ff' : '#f0f0f0',
                color: msg.role === 'user' ? '#fff' : '#000',
                whiteSpace: 'pre-wrap',
                fontSize: 13,
                lineHeight: 1.5
              }}>
                {msg.content || (isSending ? '...' : '')}
              </div>
            </List.Item>
          )}
        />
        <div ref={messagesEndRef} />
      </div>
      <div style={{ padding: 12, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8 }}>
        <Input
          placeholder="Ask about the typology logic..."
          value={inputMsg}
          onChange={(e) => setInputMsg(e.target.value)}
          onPressEnter={sendMessage}
          disabled={isSending}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={sendMessage}
          loading={isSending}
          disabled={!inputMsg.trim()}
        />
      </div>
    </Drawer>
  );
};

const ALL_TXN_CATEGORIES = [
  { value: 'incoming-all', label: 'Incoming - All', group: 'Incoming' },
  { value: 'incoming-cash', label: 'Incoming - Cash', group: 'Incoming' },
  { value: 'CDM-cash-deposit', label: 'Incoming - ATM Cash Deposit', group: 'Incoming' },
  { value: 'incoming-cheque', label: 'Incoming - Cheque', group: 'Incoming' },
  { value: 'incoming-card-payment', label: 'Incoming - Card Payment', group: 'Incoming' },
  { value: 'incoming-loan-payment', label: 'Incoming - Loan Payment', group: 'Incoming' },
  { value: 'incoming-local-fund-transfer', label: 'Incoming - Local Fund Transfer', group: 'Incoming' },
  { value: 'incoming-overseas-fund-transfer', label: 'Incoming - Overseas Fund Transfer', group: 'Incoming' },
  { value: 'incoming-e-wallet', label: 'Incoming - E-Wallet', group: 'Incoming' },
  { value: 'incoming-fund-transfer', label: 'Incoming - All Fund Transfers', group: 'Incoming' },
  { value: 'Misc-credit', label: 'Incoming - Misc Credit (refund proxy)', group: 'Incoming' },
  { value: 'outgoing-all', label: 'Outgoing - All', group: 'Outgoing' },
  { value: 'outgoing-cash', label: 'Outgoing - Cash', group: 'Outgoing' },
  { value: 'ATM-withdrawal', label: 'Outgoing - ATM Withdrawal', group: 'Outgoing' },
  { value: 'outgoing-cheque', label: 'Outgoing - Cheque', group: 'Outgoing' },
  { value: 'outgoing-card-payment', label: 'Outgoing - Card Payment', group: 'Outgoing' },
  { value: 'outgoing-loan-payment', label: 'Outgoing - Loan Payment', group: 'Outgoing' },
  { value: 'outgoing-local-fund-transfer', label: 'Outgoing - Local Fund Transfer', group: 'Outgoing' },
  { value: 'outgoing-overseas-fund-transfer', label: 'Outgoing - Overseas Fund Transfer', group: 'Outgoing' },
  { value: 'outgoing-e-wallet', label: 'Outgoing - E-Wallet', group: 'Outgoing' },
  { value: 'outgoing-fund-transfer', label: 'Outgoing - All Fund Transfers', group: 'Outgoing' },
  { value: 'loan-refund', label: 'Outgoing - Loan Refund', group: 'Outgoing' },
  { value: 'Misc-debit', label: 'Outgoing - Misc Debit', group: 'Outgoing' },
  { value: 'all-txn', label: 'All Transactions', group: 'Aggregate' },
  { value: 'all-cash', label: 'All Cash', group: 'Aggregate' },
  { value: 'all-fund-transfer', label: 'All Fund Transfers', group: 'Aggregate' },
  { value: 'all-overseas-fund-transfer', label: 'All Overseas Fund Transfers', group: 'Aggregate' },
  { value: 'high-risk-incoming', label: 'High Risk - Incoming', group: 'High Risk' },
  { value: 'high-risk-outgoing', label: 'High Risk - Outgoing', group: 'High Risk' },
  { value: 'all-high-risk', label: 'High Risk - All', group: 'High Risk' },
];

const LOOKBACK_OPTIONS = [1, 7, 14, 30, 60, 90, 180, 365];

const DynamicPropertiesPanel = ({ configuredCategories, typologyStructure, onRerunEngine }) => {
  const initial = configuredCategories || {};
  const [txnCategories, setTxnCategories] = useState(initial.txnCategories || []);
  const [lookbackDays, setLookbackDays] = useState(initial.lookbackDays || [30, 90]);
  const [customCategories, setCustomCategories] = useState(initial.customCategories || {});
  const [newCatName, setNewCatName] = useState('');
  const [newCatCodes, setNewCatCodes] = useState([]);
  const [suppressAntiFlags, setSuppressAntiFlags] = useState(false);
  const [woBinaryFlags, setWoBinaryFlags] = useState(false);
  const [userProvidedFeatures, setUserProvidedFeatures] = useState('');
  const [dpAnalysis, setDpAnalysis] = useState(null);
  const [dpImported, setDpImported] = useState({});
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (configuredCategories) {
      setTxnCategories(configuredCategories.txnCategories || []);
      setLookbackDays(configuredCategories.lookbackDays || [30, 90]);
      setCustomCategories(configuredCategories.customCategories || {});
    }
  }, [configuredCategories]);

  const handleRerun = useCallback(async () => {
    if (!typologyStructure || !onRerunEngine) return;
    setIsRunning(true);
    try {
      const engineUrl = localStorage.getItem('iad-engine-url') || 'http://localhost:8091';
      const typoId = String(typologyStructure.id || 'all');

      const dynamicProperties = {};

      // Include custom categories in the txn category list
      const allCats = [...txnCategories, ...Object.keys(customCategories)];
      if (allCats.length > 0) {
        dynamicProperties.iadTypologyTxnCategories = JSON.stringify({ [typoId]: allCats });
      }
      if (lookbackDays.length > 0) {
        dynamicProperties.iadTypologyLookBackDays = `all: ${JSON.stringify(lookbackDays)}`;
      }

      // Add custom category mappings by direction
      const inCats = {};
      const outCats = {};
      const bothCats = {};
      Object.entries(customCategories).forEach(([name, codes]) => {
        if (name.startsWith('incoming')) {
          inCats[name] = codes;
        } else if (name.startsWith('outgoing')) {
          outCats[name] = codes;
        } else if (name.startsWith('both-direction')) {
          bothCats[name] = codes;
        } else {
          // Auto-detect: if codes contain incoming, put in incoming map
          const hasIn = codes.some((c) => c.startsWith('incoming'));
          const hasOut = codes.some((c) => c.startsWith('outgoing'));
          if (hasIn && !hasOut) inCats[name] = codes;
          else if (hasOut && !hasIn) outCats[name] = codes;
          else inCats[name] = codes; // fallback
        }
      });
      if (Object.keys(inCats).length > 0) {
        dynamicProperties.iadInTxnTypeCategoryMapUpdate = JSON.stringify(inCats);
      }
      if (Object.keys(outCats).length > 0) {
        dynamicProperties.iadOutTxnTypeCategoryMapUpdate = JSON.stringify(outCats);
      }
      if (Object.keys(bothCats).length > 0) {
        dynamicProperties.iadBothDirectionAggregateCategoryMap = JSON.stringify(bothCats);
      }

      // Anti-flag suppression
      if (suppressAntiFlags) {
        dynamicProperties.iadBinaryPropertyAntiFlagConditionMap = JSON.stringify({ [typoId]: 'off' });
      }

      // W/O binary flags — generate features WITHOUT any binary flag filtering
      if (woBinaryFlags) {
        dynamicProperties.iadWoBinaryFlagsFeatsGenerationMap = JSON.stringify({ [typoId]: 'true' });
      }

      // User-provided feature map — whitelist specific features
      if (userProvidedFeatures.trim()) {
        dynamicProperties.iadUserProvidedTypologyFeatureMap = userProvidedFeatures.trim();
      }

      const engineInput = {
        name: typologyStructure.title || '',
        scenarioInstanceId: 0,
        typologies: [typologyStructure],
        dynamicProperties: dynamicProperties,
      };

      // Call via agent proxy to avoid CORS issues
      const agentApi = localStorage.getItem('service-api-agent') || 'http://localhost:8090';
      engineInput._engineUrl = engineUrl;
      const resp = await fetch(`${agentApi}/chat/engine/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(engineInput),
      });

      if (!resp.ok) throw new Error(`Engine returned ${resp.status}`);

      const engineData = await resp.json();
      const typo = typologyStructure;
      const updatedResult = {
        typologies: [{
          id: typo.id || 99000,
          title: typo.title || '',
          description: typo.description || '',
          location: typo.location || [],
          theme: typo.theme || [],
          technique: typo.technique || [],
          industry: typo.industry || [],
          predicateOffence: typo.predicateOffence || [],
          segment: typo.segment || [],
          financialInstrument: typo.financialInstrument || [],
          country: typo.country || [],
        }],
        riskIndicators: {
          typologyFeatureMap: engineData.typologyFeatureMap || {},
          businessTechFeatureMap: engineData.businessTechFeatureMap || {},
        },
        explanationGraph: engineData.explanationGraph || { explanationGraph: '{}', englishName: {} },
        dataModelFields: engineData.dataModelFields || [],
        typologyFieldMap: engineData.typologyFieldMap || {},
        indicatorLogics: engineData.indicatorLogics || [],
        _typologyStructure: typologyStructure,
        _configuredCategories: { txnCategories: allCats, lookbackDays, customCategories },
        _dynamicProperties: dynamicProperties,
      };
      onRerunEngine(updatedResult);
    } catch (err) {
      alert(`Engine error: ${err.message}`);
    } finally {
      setIsRunning(false);
    }
  }, [txnCategories, lookbackDays, typologyStructure, onRerunEngine]);

  return (
    <Card
      size="small"
      title={
        <span>
          <ExperimentOutlined style={{ marginRight: 8 }} />
          Dynamic Properties Configuration
        </span>
      }
      style={{ marginBottom: 16 }}
      extra={
        <Button
          type="primary"
          size="small"
          icon={<SearchOutlined />}
          onClick={handleRerun}
          loading={isRunning}
          disabled={!typologyStructure}
        >
          Re-run Engine
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Transaction Categories
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Select which transaction types the engine should generate indicators for. More categories = more indicators.
        </Text>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select transaction categories..."
          value={txnCategories}
          onChange={setTxnCategories}
          optionFilterProp="label"
          maxTagCount={8}
          options={ALL_TXN_CATEGORIES.map((c) => ({
            value: c.value,
            label: `${c.label} (${c.value})`,
          }))}
        />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <Button size="small" type="link" danger onClick={() => setTxnCategories([])}>
              Clear All
            </Button>
            Quick presets:{' '}
            <Button size="small" type="link" onClick={() => setTxnCategories(['incoming-all', 'outgoing-all'])}>
              All In/Out
            </Button>
            <Button size="small" type="link" onClick={() => setTxnCategories(['incoming-cash', 'outgoing-cash', 'incoming-all', 'outgoing-all'])}>
              Cash Focus
            </Button>
            <Button size="small" type="link" onClick={() => setTxnCategories(['incoming-fund-transfer', 'outgoing-fund-transfer', 'incoming-overseas-fund-transfer', 'outgoing-overseas-fund-transfer'])}>
              Fund Transfers
            </Button>
            <Button size="small" type="link" onClick={() => setTxnCategories(['high-risk-incoming', 'high-risk-outgoing', 'all-high-risk'])}>
              High Risk
            </Button>
            <Button size="small" type="link" onClick={() => setTxnCategories(['Misc-credit', 'loan-refund', 'incoming-all', 'outgoing-all'])}>
              Refund Focus
            </Button>
          </Text>
        </div>
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Lookback Days
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Time windows for indicator aggregation. Each period generates separate indicators.
        </Text>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select lookback periods..."
          value={lookbackDays}
          onChange={(vals) => setLookbackDays(vals.sort((a, b) => a - b))}
          options={LOOKBACK_OPTIONS.map((d) => ({
            value: d,
            label: `${d} days`,
          }))}
        />
      </div>

      <Divider style={{ margin: '16px 0 12px' }} />

      <div>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Extend Transaction Category Groups
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Define new Transaction Category Groups by mapping a group name to Transaction Category DB codes.
          New groups are registered via <code>iadInTxnTypeCategoryMapUpdate</code> / <code>iadOutTxnTypeCategoryMapUpdate</code> and
          automatically appended to <code>incoming-all</code> / <code>outgoing-all</code>.
        </Text>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12, fontSize: 12 }}
          message="Naming convention"
          description={
            <span>
              Incoming: <code>incoming-XXX-transfer</code> (e.g., <code>incoming-crypto-transfer</code>).{' '}
              Outgoing: <code>outgoing-XXX-transfer</code>. Only alphabetic + hyphen, no underscores or numbers.
              The DB codes (values) are the actual transaction type codes from your database
              (e.g., <code>incoming-crypto-A</code>, <code>outgoing-refund-payment</code>).
            </span>
          }
        />

        {Object.entries(customCategories).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {Object.entries(customCategories).map(([name, codes]) => (
              <Card
                key={name}
                size="small"
                style={{ marginBottom: 8, border: '1px solid #d6e4ff', background: '#f0f5ff' }}
                bodyStyle={{ padding: '8px 12px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Tag color="geekblue" style={{ fontWeight: 600, fontSize: 13 }}>{name}</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({name.startsWith('incoming') ? 'Incoming' : name.startsWith('outgoing') ? 'Outgoing' : name.startsWith('both-direction') ? 'Both Directions' : 'Auto-detect'})
                    </Text>
                  </div>
                  <Button
                    type="text" size="small" danger
                    onClick={() => {
                      const updated = { ...customCategories };
                      delete updated[name];
                      setCustomCategories(updated);
                      setTxnCategories((prev) => prev.filter((c) => c !== name));
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>DB Transaction Codes: </Text>
                  {codes.map((c) => <Tag key={c} style={{ fontSize: 11 }}>{c}</Tag>)}
                </div>
              </Card>
            ))}
          </div>
        )}

        <Card size="small" style={{ background: '#fafafa' }} bodyStyle={{ padding: '12px' }}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Add New Category Group</Text>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: '0 0 250px' }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>Group Name</Text>
              <Input
                size="small"
                placeholder="e.g., incoming-crypto-transfer"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value.toLowerCase().replace(/[^a-z-]/g, ''))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
                Transaction Category DB Codes (type to add custom codes, or select existing)
              </Text>
              <Select
                mode="tags"
                size="small"
                style={{ width: '100%' }}
                placeholder="Type DB codes or select existing categories..."
                value={newCatCodes}
                onChange={setNewCatCodes}
                tokenSeparators={[',']}
                options={[
                  { label: '── Existing Category Codes ──', value: '__header', disabled: true },
                  ...ALL_TXN_CATEGORIES.map((c) => ({ value: c.value, label: `${c.value} (${c.label})` })),
                ]}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              type="primary"
              disabled={!newCatName.trim() || newCatCodes.length === 0}
              onClick={() => {
                const name = newCatName.trim();
                if (name && newCatCodes.length > 0) {
                  setCustomCategories((prev) => ({ ...prev, [name]: newCatCodes }));
                  setTxnCategories((prev) => prev.includes(name) ? prev : [...prev, name]);
                  setNewCatName('');
                  setNewCatCodes([]);
                }
              }}
            >
              Add Category Group
            </Button>
          </div>
        </Card>
      </div>

      <Divider style={{ margin: '16px 0 12px' }} />

      <div>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Binary Flag Configuration
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Control anti-flag (NON-*) indicator generation. Anti-flag indicators represent transactions
          that do NOT match specific conditions (e.g., <code>NON-highRiskFlag</code> = transactions to non-high-risk countries).
          Suppressing them reduces indicator count but loses the comparison capability.
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: suppressAntiFlags ? '#fff7e6' : '#f6ffed', borderRadius: 4, border: `1px solid ${suppressAntiFlags ? '#ffd591' : '#b7eb8f'}` }}>
          <div style={{ flex: 1 }}>
            <Text strong>Suppress Anti-Flags (NON-* indicators)</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {suppressAntiFlags
                ? 'Anti-flag indicators will NOT be generated. Only positive flag indicators will appear.'
                : 'Anti-flag indicators ARE generated (default). Both flag and NON-flag variants are included.'
              }
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Dynamic property: <code>iadBinaryPropertyAntiFlagConditionMap</code> = {suppressAntiFlags ? `{"${typologyStructure?.id || 'all'}": "off"}` : '{}'}
            </Text>
          </div>
          <Button
            type={suppressAntiFlags ? 'primary' : 'default'}
            danger={suppressAntiFlags}
            size="small"
            onClick={() => setSuppressAntiFlags(!suppressAntiFlags)}
          >
            {suppressAntiFlags ? 'Anti-Flags: OFF' : 'Anti-Flags: ON'}
          </Button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', marginTop: 8, background: woBinaryFlags ? '#fff7e6' : '#f6ffed', borderRadius: 4, border: `1px solid ${woBinaryFlags ? '#ffd591' : '#b7eb8f'}` }}>
          <div style={{ flex: 1 }}>
            <Text strong>Without Binary Flag Features</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {woBinaryFlags
                ? 'Features generated WITHOUT any binary flag filtering. Only base sum/vol indicators.'
                : 'Binary flag features ARE generated (default). Indicators filtered by flags like highRisk, PEP, etc.'
              }
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Dynamic property: <code>iadWoBinaryFlagsFeatsGenerationMap</code> = {woBinaryFlags ? `{"${typologyStructure?.id || 'all'}": "true"}` : '{}'}
            </Text>
          </div>
          <Button
            type={woBinaryFlags ? 'primary' : 'default'}
            danger={woBinaryFlags}
            size="small"
            onClick={() => setWoBinaryFlags(!woBinaryFlags)}
          >
            {woBinaryFlags ? 'W/O Flags: ON' : 'W/O Flags: OFF'}
          </Button>
        </div>

        <div style={{ marginTop: 8 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>User-Provided Feature Whitelist</Text>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
            Restrict indicators to a specific list. Format: <code>{'{"typologyId": ["feature1", "feature2"]}'}</code>.
            This has <strong>highest priority</strong> — features in this list are always included, even if anti-flag suppression is on.
          </Text>
          <Input.TextArea
            rows={2}
            placeholder={'{"43": ["incoming-all_30day_sum", "outgoing-all_30day_vol"]}'}
            value={userProvidedFeatures}
            onChange={(e) => setUserProvidedFeatures(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: 11 }}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Dynamic property: <code>iadUserProvidedTypologyFeatureMap</code>
            {userProvidedFeatures.trim() ? ' (configured)' : ' (not set — all generated features included)'}
          </Text>
        </div>
      </div>

      <Divider style={{ margin: '16px 0 12px' }} />

      <div>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Import Dynamic Properties JSON
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Upload your deployment's dynamic properties JSON to replicate the exact environment config.
          Conflicts (e.g., user-provided features overriding anti-flag suppression) will be highlighted.
        </Text>
        <Input.TextArea
          rows={3}
          placeholder='Paste JSON content here (array format: [{"propName":"...", "propVal":"...", "category":"..."}])'
          onChange={(e) => {
            try {
              const raw = JSON.parse(e.target.value);
              const dpMap = {};
              const analysis = { conflicts: [], userFeatures: {}, antiFlagConfig: {}, woBinaryConfig: {} };

              (Array.isArray(raw) ? raw : []).forEach((item) => {
                const name = item.propName || item.name || '';
                const val = item.propVal || item.value || '';
                const cat = item.category || '';
                if (cat === 'TM_IAD' && name) {
                  dpMap[name] = val;
                }
              });

              // Parse key DPs
              const parseMap = (str) => {
                try {
                  return JSON.parse(str.replace(/'/g, '"'));
                } catch { return {}; }
              };

              if (dpMap.iadUserProvidedTypologyFeatureMap) {
                const fm = parseMap(dpMap.iadUserProvidedTypologyFeatureMap);
                Object.entries(fm).forEach(([tid, feats]) => {
                  const nonFeats = (feats || []).filter((f) => f.includes('NON-'));
                  if (nonFeats.length > 0) {
                    analysis.userFeatures[tid] = { total: feats.length, nonFlags: nonFeats };
                  }
                });
              }

              if (dpMap.iadBinaryPropertyAntiFlagConditionMap) {
                analysis.antiFlagConfig = parseMap(dpMap.iadBinaryPropertyAntiFlagConditionMap);
              }

              if (dpMap.iadWoBinaryFlagsFeatsGenerationMap) {
                analysis.woBinaryConfig = parseMap(dpMap.iadWoBinaryFlagsFeatsGenerationMap);
              }

              // Detect conflicts
              Object.entries(analysis.antiFlagConfig).forEach(([tid, val]) => {
                if (val.toLowerCase() === 'off' && analysis.userFeatures[tid]) {
                  analysis.conflicts.push({
                    typologyId: tid,
                    issue: `Anti-flag suppression is set to "off" for typology ${tid}, but iadUserProvidedTypologyFeatureMap contains ${analysis.userFeatures[tid].nonFlags.length} NON-flag features for this typology. The user-provided features take priority, so anti-flags will NOT be suppressed.`,
                    nonFlags: analysis.userFeatures[tid].nonFlags,
                    fix: `Remove the NON-flag features from iadUserProvidedTypologyFeatureMap for typology ${tid}`,
                  });
                }
              });

              setDpAnalysis(analysis);
              setDpImported(dpMap);
            } catch (err) {
              setDpAnalysis({ error: err.message });
              setDpImported({});
            }
          }}
        />

        {dpAnalysis && dpAnalysis.error && (
          <Alert message={`Parse error: ${dpAnalysis.error}`} type="error" showIcon style={{ marginTop: 8 }} />
        )}

        {dpAnalysis && dpAnalysis.conflicts && dpAnalysis.conflicts.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {dpAnalysis.conflicts.map((c, i) => (
              <Alert
                key={i}
                type="error"
                showIcon
                style={{ marginBottom: 8 }}
                message={`Conflict: Typology ${c.typologyId}`}
                description={
                  <div>
                    <Paragraph style={{ margin: 0, fontSize: 12 }}>{c.issue}</Paragraph>
                    <Text strong style={{ fontSize: 12 }}>Fix: </Text>
                    <Text style={{ fontSize: 12 }}>{c.fix}</Text>
                    <Collapse ghost style={{ marginTop: 4 }}>
                      <Panel header={`NON-flag features (${c.nonFlags.length})`} key="flags">
                        {c.nonFlags.map((f, j) => (
                          <div key={j} style={{ fontFamily: 'monospace', fontSize: 11 }}>{f}</div>
                        ))}
                      </Panel>
                    </Collapse>
                  </div>
                }
              />
            ))}
          </div>
        )}

        {dpAnalysis && !dpAnalysis.error && dpAnalysis.conflicts && dpAnalysis.conflicts.length === 0 && Object.keys(dpAnalysis.antiFlagConfig || {}).length > 0 && (
          <Alert message="No conflicts detected" type="success" showIcon style={{ marginTop: 8 }} />
        )}

        {dpAnalysis && Object.keys(dpAnalysis.antiFlagConfig || {}).length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <Text strong>Anti-Flag Config: </Text>
            {Object.entries(dpAnalysis.antiFlagConfig).map(([tid, val]) => (
              <Tag key={tid} color={val.toLowerCase() === 'off' ? 'red' : 'green'}>
                Typology {tid}: {val}
              </Tag>
            ))}
          </div>
        )}

        {dpAnalysis && Object.keys(dpAnalysis.woBinaryConfig || {}).length > 0 && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            <Text strong>W/O Binary Flags Config: </Text>
            {Object.entries(dpAnalysis.woBinaryConfig).map(([tid, val]) => (
              <Tag key={tid} color={val.toLowerCase() === 'true' ? 'orange' : 'default'}>
                Typology {tid}: {val}
              </Tag>
            ))}
          </div>
        )}

        {dpAnalysis && Object.keys(dpAnalysis.userFeatures || {}).length > 0 && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            <Text strong>User-Provided Features with NON-flags: </Text>
            {Object.entries(dpAnalysis.userFeatures).map(([tid, info]) => (
              <Tag key={tid} color="volcano">
                Typology {tid}: {info.nonFlags.length} NON-flags / {info.total} total
              </Tag>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: 12 }}>
        <Text type="secondary">
          Estimated indicators: ~{(txnCategories.length + Object.keys(customCategories).length) * lookbackDays.length * (suppressAntiFlags ? 2 : 4)} per property
          ({txnCategories.length + Object.keys(customCategories).length} categories x {lookbackDays.length} periods x ~{suppressAntiFlags ? 2 : 4} variants)
          {suppressAntiFlags ? ' (anti-flags suppressed)' : ''}
        </Text>
      </div>
    </Card>
  );
};

const CREATION_PHASES = [
  { key: 'analyze_scenario', title: 'Analyzing Scenario' },
  { key: 'plan_detection_strategy', title: 'Planning Detection Strategy' },
  { key: 'fetch_afc_typologies', title: 'Fetching AFC Typologies' },
  { key: 'generate_typology_structure', title: 'Generating Structure' },
  { key: 'configure_dynamic_properties', title: 'Configuring Properties' },
  { key: 'run_iad_engine', title: 'Running IAD Engine' },
  { key: 'summary', title: 'Complete' }
];

const { Step } = Steps;

const TypologyCreatorPanel = ({ onTypologyGenerated }) => {
  const [scenarioText, setScenarioText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [completedPhases, setCompletedPhases] = useState([]);
  const [currentPhaseMsg, setCurrentPhaseMsg] = useState('');
  const [streamedSummary, setStreamedSummary] = useState('');
  const [seedMetadata, setSeedMetadata] = useState({});
  const [anthropicKey, setAnthropicKey] = useState(localStorage.getItem('anthropic-api-key') || '');

  const currentStepIndex = completedPhases.length > 0
    ? CREATION_PHASES.findIndex((p) => p.key === completedPhases[completedPhases.length - 1])
    : -1;

  const handleGenerate = useCallback(async () => {
    if (!scenarioText.trim() || isGenerating) return;

    setIsGenerating(true);
    setGenerationError(null);
    setCompletedPhases([]);
    setCurrentPhaseMsg('');
    setStreamedSummary('');

    try {
      const agentApi = localStorage.getItem('service-api-agent') || 'http://localhost:8090';
      const token = localStorage.getItem('id_token') || '';
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      if (anthropicKey) {
        headers['X-Anthropic-Api-Key'] = anthropicKey;
      }
      const response = await fetch(`${agentApi}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: scenarioText.trim(),
          context: {
            agent_type: 'typology_creator',
            source_module: 'TM-IAD',
            extra: {
              seed_metadata: seedMetadata,
              engine_url: localStorage.getItem('iad-engine-url') || 'http://localhost:8091',
              caas_url: localStorage.getItem('service-api') || '',
              afc_token: localStorage.getItem('afc-token') || ''
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Agent API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let summary = '';
      let resultData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: step')) {
            // Next line has step data
            continue;
          } else if (line.startsWith('event: done')) {
            continue;
          } else if (line.startsWith('event: error')) {
            continue;
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              // Step event data
              if (data.phase) {
                setCompletedPhases((prev) => [...prev, data.phase]);
                setCurrentPhaseMsg(data.message || '');
              }
              // Delta event data (streaming summary)
              if (data.content) {
                summary += data.content;
                setStreamedSummary(summary);
              }
              // Done event (full result)
              if (data.agent_name && data.data) {
                resultData = data.data;
              }
              // Error event
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (e) {
              if (e.message && !e.message.startsWith('Unexpected')) {
                throw e;
              }
            }
          }
        }
      }

      if (resultData && Object.keys(resultData).length > 0) {
        onTypologyGenerated(resultData, summary);
      } else {
        setGenerationError('No typology data was generated. The agent may not have completed all steps.');
      }
    } catch (err) {
      setGenerationError(`${err.message}. Make sure the agent service is running.`);
    } finally {
      setIsGenerating(false);
    }
  }, [scenarioText, isGenerating, seedMetadata, anthropicKey, onTypologyGenerated]);

  return (
    <Card style={{ marginBottom: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <TextArea
          rows={5}
          placeholder={
            'Describe your AML/fraud scenario in detail...\n\n' +
            'Example: "Detect structuring where individuals split large cash deposits into amounts ' +
            'under the reporting threshold across multiple bank branches within a short time window. ' +
            'The pattern involves rapid successive deposits just below $10,000 with varying amounts ' +
            'to avoid detection by currency transaction reports."'
          }
          value={scenarioText}
          onChange={(e) => setScenarioText(e.target.value)}
          disabled={isGenerating}
        />

        <Collapse ghost>
          <Panel
            header={<Text type="secondary">API Key &amp; Settings</Text>}
            key="apikey"
          >
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Anthropic API Key</Text>
              <Input.Password
                placeholder="Enter your Anthropic API key (sk-ant-...)"
                value={anthropicKey}
                onChange={(e) => {
                  setAnthropicKey(e.target.value);
                  localStorage.setItem('anthropic-api-key', e.target.value);
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Required for AI generation. Stored in browser localStorage.
              </Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>IAD Engine URL</Text>
              <Input
                placeholder="http://localhost:8091"
                defaultValue={localStorage.getItem('iad-engine-url') || 'http://localhost:8091'}
                onChange={(e) => localStorage.setItem('iad-engine-url', e.target.value)}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                The IAD engine microservice URL. Default: http://localhost:8091.
                Start with: java -jar iad-engine-microservice.jar --serve 8091
              </Text>
            </div>
          </Panel>
          <Panel
            header={<Text type="secondary">Metadata Preferences (Optional)</Text>}
            key="seed"
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Theme</Text>
                <Select
                  mode="multiple"
                  placeholder="Select themes..."
                  style={{ width: '100%' }}
                  onChange={(vals) => setSeedMetadata((prev) => ({ ...prev, theme: vals }))}
                  options={[
                    'Money Laundering', 'Terrorist Financing', 'Fraud', 'Tax Evasion',
                    'Cybercrime', 'Sanctions Evasion', 'Human Trafficking'
                  ].map((v) => ({ label: v, value: v }))}
                />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Technique</Text>
                <Select
                  mode="multiple"
                  placeholder="Select techniques..."
                  style={{ width: '100%' }}
                  onChange={(vals) => setSeedMetadata((prev) => ({ ...prev, technique: vals }))}
                  options={[
                    'Structuring', 'Smurfing', 'Layering', 'Shell Companies',
                    'Trade-Based', 'Wire Transfers', 'Cash Intensive', 'Money Mules'
                  ].map((v) => ({ label: v, value: v }))}
                />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Segment</Text>
                <Select
                  mode="multiple"
                  placeholder="Select segments..."
                  style={{ width: '100%' }}
                  onChange={(vals) => setSeedMetadata((prev) => ({ ...prev, segment: vals }))}
                  options={[
                    'Retail Banking', 'Corporate Banking', 'Private Banking',
                    'Payment Services', 'Fintech', 'Cryptocurrency Exchange'
                  ].map((v) => ({ label: v, value: v }))}
                />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Financial Instrument</Text>
                <Select
                  mode="multiple"
                  placeholder="Select instruments..."
                  style={{ width: '100%' }}
                  onChange={(vals) => setSeedMetadata((prev) => ({ ...prev, financialInstrument: vals }))}
                  options={[
                    'Cash', 'Wire Transfer', 'Check', 'Credit Card',
                    'Cryptocurrency', 'Mobile Payment', 'ACH'
                  ].map((v) => ({ label: v, value: v }))}
                />
              </div>
            </div>
          </Panel>
        </Collapse>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<BulbOutlined />}
            onClick={handleGenerate}
            loading={isGenerating}
            disabled={!scenarioText.trim()}
            size="large"
          >
            Generate Typology
          </Button>
        </div>
      </Space>

      {isGenerating && (
        <div style={{ marginTop: 24 }}>
          <Steps
            size="small"
            current={currentStepIndex + 1}
            style={{ marginBottom: 16 }}
          >
            {CREATION_PHASES.map((phase, idx) => {
              const isCompleted = completedPhases.includes(phase.key);
              const isCurrent = idx === currentStepIndex + 1 && !isCompleted;
              return (
                <Step
                  key={phase.key}
                  title={phase.title}
                  icon={
                    isCompleted ? <CheckCircleOutlined /> :
                    isCurrent ? <LoadingOutlined /> :
                    <ClockCircleOutlined />
                  }
                />
              );
            })}
          </Steps>
          {currentPhaseMsg && (
            <Alert
              message={currentPhaseMsg}
              type="info"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}
          {streamedSummary && (
            <Card size="small" style={{ marginTop: 12, background: '#fafafa' }}>
              <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{streamedSummary}</Text>
            </Card>
          )}
        </div>
      )}

      {generationError && (
        <Alert
          message="Generation Failed"
          description={generationError}
          type="error"
          showIcon
          closable
          style={{ marginTop: 16 }}
        />
      )}
    </Card>
  );
};

const TypologyAnalyzer = ({
  isLoading = false,
  error = null,
  typologies = [],
  riskIndicators = null,
  explanationGraph = null,
  dataModelFields = [],
  typologyFieldMap = {},
  indicatorLogics = [],
  typologyStructure = null,
  actions = {}
}) => {
  const [mode, setMode] = useState('analyze');
  const [inputValue, setInputValue] = useState('');
  const [parsedIds, setParsedIds] = useState([]);
  const [afcToken, setAfcToken] = useState(localStorage.getItem('afc-token') || '');
  const [chatVisible, setChatVisible] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [generatedSummary, setGeneratedSummary] = useState('');
  const [chatAgentType, setChatAgentType] = useState('typology_qa');

  useEffect(() => {
    return () => {
      if (actions.resetTypologyAnalysis) {
        actions.resetTypologyAnalysis();
      }
    };
  }, []);

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setInputValue(val);
    setParsedIds(extractTypologyIds(val));
  }, []);

  const handleAnalyze = useCallback(() => {
    if (parsedIds.length === 0) return;
    actions.analyzeTypologies({
      afcTypologyIds: parsedIds,
      dynamicProperties: {},
      ...(afcToken ? { afcToken } : {})
    });
  }, [parsedIds, actions]);

  const handleTypologyGenerated = useCallback((data, summary) => {
    setGeneratedData(data);
    setGeneratedSummary(summary || '');
  }, []);

  // Determine which data to display based on mode
  const isCreateMode = mode === 'create';
  const displayData = isCreateMode && generatedData ? {
    typologies: generatedData.typologies || [],
    riskIndicators: generatedData.riskIndicators || null,
    explanationGraph: generatedData.explanationGraph || null,
    dataModelFields: generatedData.dataModelFields || [],
    typologyFieldMap: generatedData.typologyFieldMap || {},
    indicatorLogics: generatedData.indicatorLogics || []
  } : {
    typologies, riskIndicators, explanationGraph, dataModelFields, typologyFieldMap, indicatorLogics
  };

  const hasResults = displayData.typologies.length > 0 || displayData.riskIndicators ||
    displayData.explanationGraph || (displayData.dataModelFields && displayData.dataModelFields.length > 0) ||
    (displayData.indicatorLogics && displayData.indicatorLogics.length > 0);

  const analysisData = hasResults ? displayData : null;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <ApartmentOutlined style={{ marginRight: 8 }} />
            AFC Typology Analyzer
          </Title>
          <Paragraph type="secondary" style={{ margin: 0, marginTop: 4 }}>
            {isCreateMode
              ? 'Describe an AML/fraud scenario to auto-generate a typology with risk indicators.'
              : 'Enter AFC typology URLs or IDs to analyze their risk indicators and data model requirements.'}
          </Paragraph>
        </div>
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          buttonStyle="solid"
          size="large"
        >
          <Radio.Button value="analyze"><SearchOutlined /> Analyze Existing</Radio.Button>
          <Radio.Button value="create"><BulbOutlined /> Create from Scenario</Radio.Button>
        </Radio.Group>
      </div>

      {isCreateMode ? (
        <TypologyCreatorPanel onTypologyGenerated={handleTypologyGenerated} />
      ) : (
        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <TextArea
              rows={3}
              placeholder={
                'Enter AFC typology URLs or IDs (one per line, or comma-separated)\n' +
                'e.g., https://community.afcecosystem.org/preview/1883\n' +
                'e.g., 1883, 1437'
              }
              value={inputValue}
              onChange={handleInputChange}
            />

            <Collapse ghost>
              <Panel header={<Text type="secondary">AFC Token Settings</Text>} key="token">
                <Input.Password
                  placeholder="Paste your AFC authorization token from browser DevTools"
                  value={afcToken}
                  onChange={(e) => {
                    setAfcToken(e.target.value);
                    localStorage.setItem('afc-token', e.target.value);
                  }}
                  style={{ marginBottom: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Get token from AFC portal: Chrome DevTools &gt; Network &gt; any request &gt; Authorization header
                </Text>
              </Panel>
            </Collapse>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {parsedIds.length > 0 && (
                  <Text type="secondary">
                    Detected IDs:{' '}
                    {parsedIds.map((id) => (
                      <Tag key={id} color="blue">AFC-{id}</Tag>
                    ))}
                  </Text>
                )}
              </div>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleAnalyze}
                loading={isLoading}
                disabled={parsedIds.length === 0}
              >
                Analyze Typologies
              </Button>
            </div>
          </Space>
        </Card>
      )}

      {error && (
        <Alert
          message="Analysis Failed"
          description={typeof error === 'string' ? error : 'An error occurred while analyzing typologies.'}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" tip="Analyzing typologies..." />
        </div>
      )}

      {!isLoading && hasResults && (
        <div>
          {isCreateMode && generatedSummary && (
            <Alert
              message="AI-Generated Typology"
              description={generatedSummary}
              type={generatedData && generatedData._engineStatus ? 'warning' : 'success'}
              showIcon
              icon={<BulbOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}
          {isCreateMode && generatedData && generatedData._generationProcess && (
            <Card
              size="small"
              style={{ marginBottom: 16 }}
              title={
                <span>
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  Generation Process — Reasoning & Property Selection
                </span>
              }
            >
              <Collapse ghost>
                {generatedData._generationProcess.analysis && generatedData._generationProcess.analysis.scenario_summary && (
                  <Panel header="Step 1: Scenario Analysis" key="analysis">
                    <Paragraph><strong>Summary:</strong> {generatedData._generationProcess.analysis.scenario_summary}</Paragraph>
                    {generatedData._generationProcess.analysis.entities && (
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>Entities Identified:</Text>
                        <div style={{ marginTop: 4 }}>
                          {generatedData._generationProcess.analysis.entities.map((e, i) => (
                            <Tag key={i} color={e.type === 'individual' ? 'blue' : e.type === 'corporate' ? 'orange' : 'default'}>
                              {e.name} ({e.type}) — {e.role}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                    {generatedData._generationProcess.analysis.risk_patterns && (
                      <div>
                        <Text strong>Risk Patterns:</Text>
                        <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                          {generatedData._generationProcess.analysis.risk_patterns.map((p, i) => (
                            <li key={i} style={{ fontSize: 12 }}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Panel>
                )}

                {generatedData._generationProcess.detectionPlan && generatedData._generationProcess.detectionPlan.detection_needs && (
                  <Panel header={`Step 2: Detection Strategy — ${generatedData._generationProcess.detectionPlan.detection_needs.length} detection needs mapped`} key="plan">
                    <Table
                      size="small"
                      bordered
                      pagination={false}
                      dataSource={generatedData._generationProcess.detectionPlan.detection_needs.map((d, i) => ({ ...d, key: i }))}
                      columns={[
                        {
                          title: 'Detection Need', dataIndex: 'need', key: 'need', width: 250,
                          render: (v) => <Text strong style={{ fontSize: 12 }}>{v}</Text>
                        },
                        {
                          title: 'Selected Properties', dataIndex: 'properties', key: 'properties', width: 200,
                          render: (props) => (props || []).map((p, i) => (
                            <Tag key={i} color="blue" style={{ fontSize: 11, marginBottom: 2 }}>{p}</Tag>
                          ))
                        },
                        {
                          title: 'Trigger Attributes', dataIndex: 'trigger_attributes', key: 'triggers',
                          render: (attrs) => (attrs || []).map((a, i) => (
                            <Tag key={i} style={{ fontSize: 11, marginBottom: 2 }}>{a}</Tag>
                          ))
                        },
                        {
                          title: 'Reasoning', dataIndex: 'reasoning', key: 'reasoning',
                          render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text>
                        },
                      ]}
                    />
                    {generatedData._generationProcess.detectionPlan.selected_properties_summary && (
                      <div style={{ marginTop: 12 }}>
                        <Text strong>All Selected Properties: </Text>
                        {generatedData._generationProcess.detectionPlan.selected_properties_summary.map((p, i) => (
                          <Tag key={i} color="geekblue" style={{ marginBottom: 2 }}>{p}</Tag>
                        ))}
                      </div>
                    )}
                  </Panel>
                )}

                {generatedData._generationProcess.reasoningTrace && (
                  <Panel header="Step-by-Step Execution Log" key="trace">
                    <div style={{ fontSize: 12 }}>
                      {generatedData._generationProcess.reasoningTrace
                        .filter((t) => !t.startsWith('[summary]'))
                        .map((step, i) => {
                          const match = step.match(/^\[([^\]]+)\]\s*(.*)/);
                          return (
                            <div key={i} style={{ marginBottom: 6, display: 'flex', gap: 8 }}>
                              <Tag color="processing" style={{ flexShrink: 0, fontSize: 11 }}>
                                {match ? match[1] : `Step ${i + 1}`}
                              </Tag>
                              <Text style={{ fontSize: 12 }}>{match ? match[2] : step}</Text>
                            </div>
                          );
                        })}
                    </div>
                  </Panel>
                )}
              </Collapse>
            </Card>
          )}

          {isCreateMode && generatedData && generatedData._engineStatus && (
            <Alert
              message="IAD Engine Not Connected"
              description={
                'The typology structure was generated successfully, but risk indicators, data model, ' +
                'and explanation graph require the Scala CAAS backend with the /typologyAnalysisDirect endpoint. ' +
                'To get real production indicators: 1) Deploy the CAAS backend with the new endpoint, ' +
                '2) Set the CAAS Backend URL in the API Key & Settings section above, 3) Re-generate.'
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {isCreateMode && generatedData && generatedData._typologyStructure && (
            <Card
              size="small"
              title="Generated Typology Graph Structure"
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    downloadFile(
                      JSON.stringify(generatedData._typologyStructure, null, 2),
                      'typology-structure.json',
                      'application/json'
                    );
                  }}
                >
                  Download Structure JSON
                </Button>
              }
            >
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="Nodes">
                  {(generatedData._typologyStructure.nodes || []).map((n, i) => (
                    <Tag key={i} color={n.nodeType === 1 ? 'blue' : n.nodeType === 2 ? 'orange' : 'default'}>
                      {n.name} ({n.nodeType === 1 ? 'Individual' : n.nodeType === 2 ? 'Corporate' : 'Any'})
                    </Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Relations">
                  {(generatedData._typologyStructure.relations || []).length} relation(s)
                </Descriptions.Item>
              </Descriptions>
              <Collapse ghost style={{ marginTop: 8 }}>
                <Panel header="View full structure JSON" key="json">
                  <pre style={{ fontSize: 11, maxHeight: 300, overflow: 'auto', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                    {JSON.stringify(generatedData._typologyStructure, null, 2)}
                  </pre>
                </Panel>
              </Collapse>
            </Card>
          )}

          {((isCreateMode && generatedData && generatedData._typologyStructure) ||
            (!isCreateMode && typologyStructure)) && (
            <DynamicPropertiesPanel
              configuredCategories={isCreateMode ? generatedData?._configuredCategories : null}
              typologyStructure={isCreateMode ? generatedData?._typologyStructure : typologyStructure}
              onRerunEngine={(updatedResult) => {
                if (isCreateMode) {
                  setGeneratedData(updatedResult);
                  setGeneratedSummary('Indicators re-generated with updated configuration.');
                } else {
                  // For Analyze Existing, dispatch updated results to Redux
                  actions.analyzeTypologiesSuccess(updatedResult);
                }
              }}
            />
          )}

          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportFullJson(displayData.typologies, displayData.riskIndicators, displayData.explanationGraph, displayData.dataModelFields, displayData.indicatorLogics)}
              >
                Download All (JSON)
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportOverviewCsv(displayData.typologies)}
              >
                Overview CSV
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportRiskIndicatorsCsv(displayData.riskIndicators)}
                disabled={!displayData.riskIndicators}
              >
                Risk Indicators CSV
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportDataModelCsv(displayData.dataModelFields, displayData.typologyFieldMap)}
                disabled={!displayData.dataModelFields || displayData.dataModelFields.length === 0}
              >
                Data Model CSV
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportKeyLogicCsv(displayData.indicatorLogics)}
                disabled={!displayData.indicatorLogics || displayData.indicatorLogics.length === 0}
              >
                Key Logic CSV
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportExplanationGraphCsv(displayData.explanationGraph)}
                disabled={!displayData.explanationGraph}
              >
                Explanation Graph CSV
              </Button>
              <Badge dot={chatVisible}>
                <Button
                  type="primary"
                  ghost
                  icon={<MessageOutlined />}
                  onClick={() => {
                    setChatAgentType(isCreateMode ? 'typology_creator' : 'typology_qa');
                    setChatVisible(true);
                  }}
                >
                  {isCreateMode ? 'Refine with AI' : 'Ask AI'}
                </Button>
              </Badge>
            </Space>
          </div>

          <Tabs defaultActiveKey="overview" type="card">
            <TabPane
              tab={<span><InfoCircleOutlined /> Overview ({displayData.typologies.length})</span>}
              key="overview"
            >
              {displayData.typologies.map((t) => (
                <TypologyInfoCard
                  key={t.id}
                  typology={t}
                  typologyStructure={isCreateMode ? generatedData?._typologyStructure : typologyStructure}
                  onStructureChange={isCreateMode ? (updatedStructure) => {
                    setGeneratedData((prev) => ({ ...prev, _typologyStructure: updatedStructure }));
                  } : undefined}
                />
              ))}
            </TabPane>

            <TabPane
              tab={<span><AlertOutlined /> Risk Indicators</span>}
              key="riskIndicators"
            >
              <RiskIndicatorsTab riskIndicators={displayData.riskIndicators} />
            </TabPane>

            <TabPane
              tab={<span><DatabaseOutlined /> Data Model ({displayData.dataModelFields ? displayData.dataModelFields.length : 0})</span>}
              key="dataModel"
            >
              <DataModelTab dataModelFields={displayData.dataModelFields} typologyFieldMap={displayData.typologyFieldMap} />
            </TabPane>

            <TabPane
              tab={<span><ExperimentOutlined /> Key Logic ({displayData.indicatorLogics ? displayData.indicatorLogics.length : 0})</span>}
              key="keyLogic"
            >
              <KeyLogicTab indicatorLogics={displayData.indicatorLogics} />
            </TabPane>

            <TabPane
              tab={<span><FileTextOutlined /> Explanation Graph</span>}
              key="explanationGraph"
            >
              <ExplanationGraphTab explanationGraph={displayData.explanationGraph} />
            </TabPane>
          </Tabs>
        </div>
      )}

      <TypologyChatPanel
        visible={chatVisible}
        onClose={() => setChatVisible(false)}
        analysisData={isCreateMode ? {
          ...analysisData,
          _typologyStructure: generatedData?._typologyStructure,
          _engineUrl: localStorage.getItem('iad-engine-url') || 'http://localhost:8091',
          _caasUrl: localStorage.getItem('service-api') || '',
          _afcToken: localStorage.getItem('afc-token') || '',
        } : analysisData}
        agentType={chatAgentType}
        onRefinementResult={isCreateMode ? (data, summary) => {
          setGeneratedData(data);
          if (summary) setGeneratedSummary(summary);
        } : undefined}
      />
    </div>
  );
};

export default TypologyAnalyzer;
